import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import MonitorCard from "../components/MonitorCard";
import {
  getMonitorGroup, updateMonitorGroup, deleteMonitorGroup,
  listMonitors, listMonitorGroups, createMonitor, setMonitorGroup, deleteMonitor,
  CreateMonitorGroupParams, CreateMonitorParams, ApiError,
} from "../lib/api";
import { Monitor, MonitorGroup, ALL_REGIONS } from "../types";
import { Bone } from "../components/Skeleton";

const GROUP_COLORS = [
  "#FF2566", "#FF5C5C", "#FF4081", "#E91E63", "#D32F2F", "#C62828",
  "#FF9800", "#FF7043", "#FF6D00", "#E64A19", "#BF360C", "#FF8F00",
  "#FDDD48", "#FFC107", "#FFB300", "#C6A700", "#8BC34A", "#558B2F",
  "#39D98A", "#00BFA5", "#26A69A", "#00897B", "#43A047", "#2E7D32",
  "#2196F3", "#1976D2", "#0D47A1", "#29B6F6", "#0288D1", "#01579B",
  "#6366F1", "#7B1FA2", "#673AB7", "#512DA8", "#9C27B0", "#AB47BC",
];

const PRO_INTERVALS = [
  { value: 1,  label: "Every 1 min" },
  { value: 5,  label: "Every 5 min" },
  { value: 10, label: "Every 10 min" },
  { value: 15, label: "Every 15 min" },
  { value: 30, label: "Every 30 min" },
  { value: 60, label: "Every 1 hour" },
];

export default function MonitorGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [showCreateMonitor, setShowCreateMonitor] = useState(false);
  const [showAddMonitor, setShowAddMonitor] = useState(false);

  const { data: group, isLoading: groupLoading, isError: groupError, error: groupFetchError } = useQuery({
    queryKey: ["monitor-groups", id],
    queryFn: () => getMonitorGroup(id!),
    enabled: !!id,
  });

  const { data: monitorsData, isLoading: monitorsLoading, isError: monitorsError } = useQuery({
    queryKey: ["monitors"],
    queryFn: listMonitors,
  });

  const { data: groupsData } = useQuery({
    queryKey: ["monitor-groups"],
    queryFn: listMonitorGroups,
  });

  const allMonitors: Monitor[] = monitorsData?.monitors ?? [];
  const allGroups: MonitorGroup[] = groupsData?.groups ?? [];
  const groupMap = new Map(allGroups.map(g => [g.id, g]));

  const groupMonitors = allMonitors.filter(m => m.group_id === id);
  const otherMonitors = allMonitors.filter(m => m.group_id !== id);

  const deleteGroupMut = useMutation({
    mutationFn: () => deleteMonitorGroup(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["monitor-groups"] });
      qc.invalidateQueries({ queryKey: ["monitors"] });
      navigate("/dashboard/monitors");
    },
  });

  const removeMonitorMut = useMutation({
    mutationFn: (monitorId: string) => setMonitorGroup(monitorId, null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monitors"] }),
  });

  const deleteMonitorMut = useMutation({
    mutationFn: (monitorId: string) => deleteMonitor(monitorId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monitors"] }),
  });

  const isLoading = groupLoading || monitorsLoading;

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
          <Bone w={200} h={28} style={{ marginBottom: 12 }} />
          <Bone w={300} h={14} style={{ marginBottom: 32 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[140, 190, 160].map((w, i) => (
              <div key={i} className="glass" style={{ padding: "16px 20px" }}>
                <Bone w={w} h={15} style={{ marginBottom: 8 }} />
                <Bone w={w + 60} h={11} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (groupError) {
    const is404 = groupFetchError instanceof ApiError && groupFetchError.status === 404;
    if (is404) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center", color: "#98A1AB" }}>
            <p style={{ fontSize: 16, marginBottom: 12 }}>Group not found.</p>
            <Link to="/dashboard/monitors" style={{ color: "#FF2566", textDecoration: "none", fontSize: 14 }}>← Back to Monitors</Link>
          </div>
        </div>
      );
    }
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#98A1AB", padding: "0 24px" }}>
          <p style={{ fontSize: 16, color: "#EFF2F5", marginBottom: 8 }}>Service temporarily unavailable</p>
          <p style={{ fontSize: 14, marginBottom: 20 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          <button onClick={() => window.location.reload()} className="btn-glass" style={{ padding: "8px 20px", fontSize: 13 }}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  if (monitorsError) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#98A1AB", padding: "0 24px" }}>
          <p style={{ fontSize: 16, color: "#EFF2F5", marginBottom: 8 }}>Service temporarily unavailable</p>
          <p style={{ fontSize: 14, marginBottom: 20 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          <button onClick={() => window.location.reload()} className="btn-glass" style={{ padding: "8px 20px", fontSize: 13 }}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#98A1AB" }}>
          <p style={{ fontSize: 16, marginBottom: 12 }}>Group not found.</p>
          <Link to="/dashboard/monitors" style={{ color: "#FF2566", textDecoration: "none", fontSize: 14 }}>← Back to Monitors</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Color accent header strip */}
      <div style={{ height: 4, background: group.color }} />

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "32px 24px" }}>
        {/* Breadcrumb */}
        <Link
          to="/dashboard/monitors"
          style={{ fontSize: 13, color: "#98A1AB", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 20 }}
        >
          ← Monitors
        </Link>

        {/* Group header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: group.color, display: "inline-block", flexShrink: 0 }} />
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#EFF2F5" }}>{group.name}</h1>
            </div>
            {group.description && (
              <p style={{ margin: 0, fontSize: 14, color: "#98A1AB", lineHeight: 1.6 }}>{group.description}</p>
            )}
            <p style={{ margin: "6px 0 0", fontSize: 12, color: "#98A1AB" }}>
              {groupMonitors.length} {groupMonitors.length === 1 ? "monitor" : "monitors"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => { setShowEdit(v => !v); setShowCreateMonitor(false); setShowAddMonitor(false); }}
              style={{
                padding: "8px 14px", fontSize: 13, cursor: "pointer",
                background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                color: "#EFF2F5", borderRadius: 3,
              }}
            >
              {showEdit ? "Cancel Edit" : "Edit Group"}
            </button>
            <button
              onClick={() => {
                if (confirm(`Delete group "${group.name}"? Monitors will not be deleted.`)) {
                  deleteGroupMut.mutate();
                }
              }}
              style={{
                padding: "8px 14px", fontSize: 13, cursor: "pointer",
                background: "transparent", border: "1px solid rgba(255,92,92,0.4)",
                color: "#FF5C5C", borderRadius: 3,
              }}
            >
              Delete Group
            </button>
          </div>
        </div>

        {/* Edit group form */}
        {showEdit && (
          <EditGroupForm
            group={group}
            onSuccess={() => {
              setShowEdit(false);
              qc.invalidateQueries({ queryKey: ["monitor-groups", id] });
            }}
          />
        )}

        {/* Monitor action buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <button
            onClick={() => { setShowCreateMonitor(v => !v); setShowAddMonitor(false); setShowEdit(false); }}
            className="btn-primary"
            style={{ padding: "8px 16px", fontSize: 13 }}
          >
            {showCreateMonitor ? "Cancel" : "+ Create Monitor"}
          </button>
          {allMonitors.length > groupMonitors.length && (
            <button
              onClick={() => { setShowAddMonitor(v => !v); setShowCreateMonitor(false); setShowEdit(false); }}
              style={{
                padding: "8px 16px", fontSize: 13, cursor: "pointer",
                background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                color: "#EFF2F5", borderRadius: 3,
              }}
            >
              {showAddMonitor ? "Cancel" : "Add Existing Monitor"}
            </button>
          )}
        </div>

        {/* Create monitor form */}
        {showCreateMonitor && (
          <CreateMonitorForm
            groupId={id!}
            onSuccess={() => {
              setShowCreateMonitor(false);
              qc.invalidateQueries({ queryKey: ["monitors"] });
            }}
          />
        )}

        {/* Add existing monitor panel */}
        {showAddMonitor && (
          <AddMonitorPanel
            groupId={id!}
            groupColor={group.color}
            candidates={otherMonitors}
            groupMap={groupMap}
            onDone={() => {
              setShowAddMonitor(false);
              qc.invalidateQueries({ queryKey: ["monitors"] });
            }}
          />
        )}

        {/* Monitor list */}
        {groupMonitors.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 15 }}>No monitors in this group yet.</p>
            <p style={{ fontSize: 13 }}>Create a new monitor or add an existing one.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {groupMonitors.map(m => (
              <MonitorCard
                key={m.id}
                monitor={m}
                linkState={{ fromGroup: { id: group.id, name: group.name } }}
                onRemoveFromGroup={() => removeMonitorMut.mutate(m.id)}
                onDelete={() => {
                  if (confirm(`Delete monitor "${m.name}"?`)) {
                    deleteMonitorMut.mutate(m.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EditGroupForm({ group, onSuccess }: { group: MonitorGroup; onSuccess: () => void }) {
  const [color, setColor] = useState(group.color);
  const [showPicker, setShowPicker] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { name: group.name, description: group.description ?? "" },
  });

  const mut = useMutation({
    mutationFn: (params: CreateMonitorGroupParams) => updateMonitorGroup(group.id, params),
    onSuccess,
    onError: (e) => setApiError(e instanceof Error ? e.message : "Failed to update group"),
  });

  function onSubmit(values: { name: string; description: string }) {
    mut.mutate({ name: values.name, description: values.description || null, color });
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="glass"
      style={{ padding: 20, marginBottom: 20, display: "flex", flexDirection: "column", gap: 14 }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#EFF2F5" }}>Edit Group</h3>
      {apiError && <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>{apiError}</p>}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
        <FormField label="Name" error={errors.name?.message} style={{ flex: 1 }}>
          <input {...register("name", { required: "Name is required" })} className="input-dark" />
        </FormField>
        <FormField label="Color">
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setShowPicker(v => !v)}
              style={{ width: 38, height: 38, borderRadius: 3, border: "2px solid rgba(255,255,255,0.2)", background: color, cursor: "pointer", display: "block" }}
            />
            {showPicker && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 8px)", left: 0, zIndex: 100,
                background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 6, padding: 10,
                display: "grid", gridTemplateColumns: "repeat(6, 24px)", gap: 5,
                boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
              }}>
                {GROUP_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => { setColor(c); setShowPicker(false); }}
                    style={{ width: 24, height: 24, borderRadius: 3, background: c, cursor: "pointer", border: c === color ? "2px solid #fff" : "2px solid transparent", padding: 0 }} />
                ))}
              </div>
            )}
          </div>
        </FormField>
      </div>

      <FormField label="Description (optional)">
        <textarea {...register("description")} className="input-dark" rows={2} style={{ resize: "vertical", minHeight: 60 }} />
      </FormField>

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={mut.isPending} className="btn-primary" style={{ padding: "9px 20px", fontSize: 13 }}>
          {mut.isPending ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}

interface CreateMonitorFormValues {
  name: string;
  url: string;
  method: string;
  interval_minutes: number;
  alert_latency_ms: string;
  alert_cert_expiry_days: string;
  alert_email: string;
  alert_webhook_url: string;
}

function CreateMonitorForm({ groupId, onSuccess }: { groupId: string; onSuccess: () => void }) {
  const [apiError, setApiError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<CreateMonitorFormValues>({
    defaultValues: { method: "GET", interval_minutes: 5 },
  });

  const mut = useMutation({
    mutationFn: (params: CreateMonitorParams) => createMonitor(params),
    onSuccess,
    onError: (e) => setApiError(e instanceof Error ? e.message : "Failed to create monitor"),
  });

  function onSubmit(values: CreateMonitorFormValues) {
    mut.mutate({
      name: values.name,
      url: values.url,
      method: values.method,
      interval_minutes: Number(values.interval_minutes),
      regions: ALL_REGIONS,
      group_id: groupId,
      alert_latency_ms: values.alert_latency_ms ? Number(values.alert_latency_ms) : undefined,
      alert_cert_expiry_days: values.alert_cert_expiry_days ? Number(values.alert_cert_expiry_days) : undefined,
      alert_email: values.alert_email || undefined,
      alert_webhook_url: values.alert_webhook_url || undefined,
    });
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="glass"
      style={{ padding: 20, marginBottom: 20, display: "flex", flexDirection: "column", gap: 14 }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#EFF2F5" }}>New Monitor</h3>
      {apiError && <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>{apiError}</p>}

      <FormField label="Name" error={errors.name?.message}>
        <input {...register("name", { required: "Name is required" })} className="input-dark" placeholder="API Health Check" />
      </FormField>
      <FormField label="URL" error={errors.url?.message}>
        <input {...register("url", { required: "URL is required" })} className="input-dark" placeholder="https://api.example.com/health" />
      </FormField>

      <div style={{ display: "flex", gap: 12 }}>
        <FormField label="Method">
          <select {...register("method")} className="input-dark">
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </select>
        </FormField>
        <FormField label="Interval">
          <select {...register("interval_minutes")} className="input-dark">
            {PRO_INTERVALS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </FormField>
      </div>

      <details>
        <summary style={{ cursor: "pointer", fontSize: 13, color: "#98A1AB" }}>Alert settings (optional)</summary>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
          <FormField label="Alert if P95 exceeds (ms)">
            <input {...register("alert_latency_ms")} className="input-dark" type="number" placeholder="1000" />
          </FormField>
          <FormField label="Alert when SSL cert expires within (days)">
            <input {...register("alert_cert_expiry_days")} className="input-dark" type="number" placeholder="30" />
          </FormField>
          <FormField label="Alert email">
            <input {...register("alert_email")} className="input-dark" type="email" placeholder="you@example.com" />
          </FormField>
          <FormField label="Webhook URL">
            <input {...register("alert_webhook_url")} className="input-dark" placeholder="https://hooks.example.com/worldrtt" />
          </FormField>
        </div>
      </details>

      <button type="submit" disabled={mut.isPending} className="btn-primary" style={{ padding: "9px 0", fontSize: 13 }}>
        {mut.isPending ? "Creating…" : "Create Monitor"}
      </button>
    </form>
  );
}

function AddMonitorPanel({ groupId, groupColor, candidates, groupMap, onDone }: {
  groupId: string;
  groupColor: string;
  candidates: Monitor[];
  groupMap: Map<string, MonitorGroup>;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isPending, setIsPending] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function doAdd() {
    setIsPending(true);
    try {
      await Promise.all(Array.from(selected).map(id => setMonitorGroup(id, groupId)));
      onDone();
    } finally {
      setIsPending(false);
    }
  }

  function handleAdd() {
    if (selected.size === 0) return;

    const conflicts = candidates.filter(m => selected.has(m.id) && m.group_id !== null);
    if (conflicts.length > 0) {
      const lines = conflicts.map(m => {
        const name = groupMap.get(m.group_id!)?.name ?? "another group";
        return `"${m.name}" is currently in "${name}"`;
      });
      const message = conflicts.length === 1
        ? `"${conflicts[0].name}" is currently in "${groupMap.get(conflicts[0].group_id!)?.name ?? "another group"}". Adding it here will remove it from that group.`
        : `The following monitors will be removed from their current groups:\n\n${lines.join("\n")}`;
      setConfirmModal({ title: "Move monitors to this group?", message, onConfirm: doAdd });
      return;
    }

    doAdd();
  }

  return (
    <>
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="Move to Group"
          onConfirm={confirmModal.onConfirm}
          onClose={() => setConfirmModal(null)}
        />
      )}
      <div
        className="glass"
        style={{ padding: 20, marginBottom: 20 }}
      >
        <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: "#EFF2F5" }}>Add Existing Monitors</h3>
        <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          {candidates.map(m => (
            <label
              key={m.id}
              style={{
                display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                padding: "8px 10px", borderRadius: 3,
                background: selected.has(m.id) ? "rgba(255,255,255,0.06)" : "transparent",
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(m.id)}
                onChange={() => toggle(m.id)}
                style={{ accentColor: groupColor, width: 14, height: 14, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: "#EFF2F5", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
              <span style={{ fontSize: 12, color: "#98A1AB", flexShrink: 0, marginLeft: "auto" }}>{m.url.replace(/^https?:\/\//, "")}</span>
            </label>
          ))}
        </div>
        <button
          onClick={handleAdd}
          disabled={selected.size === 0 || isPending}
          className="btn-primary"
          style={{ padding: "8px 20px", fontSize: 13, opacity: selected.size === 0 ? 0.5 : 1 }}
        >
          {isPending ? "Adding…" : `Add ${selected.size > 0 ? selected.size : ""} Monitor${selected.size !== 1 ? "s" : ""}`}
        </button>
      </div>
    </>
  );
}

function FormField({ label, children, error, style }: { label: string; children: React.ReactNode; error?: string; style?: React.CSSProperties }) {
  return (
    <div style={{ flex: 1, ...style }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#98A1AB", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </label>
      {children}
      {error && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#FF5C5C" }}>{error}</p>}
    </div>
  );
}
